import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Row,
  Col,
  Input,
  Avatar,
  Tag,
  Card,
  Switch,
  Tooltip,
  Button,
  Space,
  Spin,
  Empty,
  Typography,
  Select,
} from "antd";
import {
  UserOutlined,
  SaveOutlined,
  UndoOutlined,
  SearchOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import { getHeaders } from "../../utility/getHeader";

const { Text, Title } = Typography;
const ACCENT = "#001529";

const ROLE_LABELS = {
  cashier: "Cashier",
  promoter: "Promoter",
  account: "Account",
  cstoremanager: "Club Store Manager",
  wstoremanager: "Warehouse Store Manager",
  modifier: "Modifier",
  hr: "HR",
};

const ROLE_TAG_COLORS = {
  cashier: "green",
  promoter: "gold",
  account: "geekblue",
  cstoremanager: "purple",
  wstoremanager: "volcano",
  modifier: "magenta",
  hr: "cyan",
};

export default function PagePermissions() {
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [pages, setPages] = useState([]); // effective permissions for selected user
  const [pending, setPending] = useState({}); // page_code -> boolean, only entries touched this session

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedUserId) loadPermissions(selectedUserId);
  }, [selectedUserId]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await axios.get("/page-permissions/users", getHeaders());
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error loading users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadPermissions = async (userId) => {
    setLoadingPermissions(true);
    setPending({});
    try {
      const res = await axios.get(`/page-permissions/effective/${userId}`, getHeaders());
      setPages(res.data?.pages || []);
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error loading permissions");
      setPages([]);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter((u) => {
      const role = (u.type || "").toLowerCase();
      const matchesRole = roleFilter === "all" || role === roleFilter;
      const matchesTerm =
        !term ||
        u.name?.toLowerCase().includes(term) ||
        u.uname?.toLowerCase().includes(term) ||
        String(u.id).includes(term);
      return matchesRole && matchesTerm;
    });
  }, [users, search, roleFilter]);

  const usersByRole = useMemo(() => {
    const groups = {};
    filteredUsers.forEach((u) => {
      const role = (u.type || "").toLowerCase();
      if (!groups[role]) groups[role] = [];
      groups[role].push(u);
    });
    return groups;
  }, [filteredUsers]);

  const pagesByCategory = useMemo(() => {
    const groups = {};
    pages.forEach((p) => {
      if (!groups[p.category]) groups[p.category] = [];
      groups[p.category].push(p);
    });
    return groups;
  }, [pages]);

  const selectedUser = users.find((u) => u.id === selectedUserId);

  const effectiveValue = (page) =>
    Object.prototype.hasOwnProperty.call(pending, page.code) ? pending[page.code] : page.effective;

  const isOverridden = (page) =>
    Object.prototype.hasOwnProperty.call(pending, page.code)
      ? pending[page.code] !== page.roleDefault
      : page.hasOverride;

  const handleToggle = (page, checked) => {
    setPending((prev) => ({ ...prev, [page.code]: checked }));
  };

  const handleResetOne = (page) => {
    setPending((prev) => ({ ...prev, [page.code]: page.roleDefault }));
  };

  const handleResetAll = () => {
    const reset = {};
    pages.forEach((p) => {
      reset[p.code] = p.roleDefault;
    });
    setPending(reset);
  };

  const hasPendingChanges = useMemo(() => {
    return pages.some(
      (p) => Object.prototype.hasOwnProperty.call(pending, p.code) && pending[p.code] !== p.effective
    );
  }, [pages, pending]);

  const handleSave = async () => {
    if (!selectedUserId) return;
    const changes = [];
    const resets = [];

    pages.forEach((p) => {
      if (!Object.prototype.hasOwnProperty.call(pending, p.code)) return;
      const nextValue = pending[p.code];
      if (nextValue === p.effective) return; // no actual change
      if (nextValue === p.roleDefault) {
        resets.push(p.code);
      } else {
        changes.push({ page_code: p.code, is_enabled: nextValue });
      }
    });

    if (changes.length === 0 && resets.length === 0) {
      toast.info("Nothing to save");
      return;
    }

    try {
      setSaving(true);
      const res = await axios.put(
        `/page-permissions/user/${selectedUserId}`,
        { changes, resets },
        getHeaders()
      );
      setPages(res.data?.pages || []);
      setPending({});
      toast.success(`Permissions updated for ${selectedUser?.name || "user"}`);
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error saving permissions");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Header title="Page Permissions" />

      <div style={{ padding: "16px", maxWidth: 1600, margin: "0 auto", background: "#f5f6fa" }}>
        <Row gutter={[14, 14]}>
          {/* Left: user picker */}
          <Col xs={24} lg={7} xl={6} xxl={5}>
            <Card
              bordered={false}
              size="small"
              bodyStyle={{ padding: "12px 12px 4px" }}
              style={{ borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
              title={<Text strong style={{ fontSize: 13.5 }}>Users</Text>}
            >
              <Select
                value={roleFilter}
                onChange={setRoleFilter}
                size="small"
                style={{ width: "100%", marginBottom: 8 }}
                options={[
                  { value: "all", label: `All Roles · ${users.length}` },
                  ...Object.keys(ROLE_LABELS).map((role) => ({
                    value: role,
                    label: `${ROLE_LABELS[role]} · ${users.filter((u) => (u.type || "").toLowerCase() === role).length}`,
                  })),
                ]}
              />

              <Input
                placeholder="Search by name, username or id"
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                size="small"
                style={{ marginBottom: 10 }}
              />

              <Spin spinning={loadingUsers}>
                {Object.keys(usersByRole).length === 0 && !loadingUsers ? (
                  <Empty description="No users found" />
                ) : (
                  Object.entries(usersByRole).map(([role, roleUsers]) => (
                    <div key={role} style={{ marginBottom: 10 }}>
                      <Tag
                        color={ROLE_TAG_COLORS[role] || "default"}
                        style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, marginBottom: 4 }}
                      >
                        {(ROLE_LABELS[role] || role).toUpperCase()} · {roleUsers.length}
                      </Tag>
                      <div>
                        {roleUsers.map((u) => (
                          <div
                            key={u.id}
                            onClick={() => setSelectedUserId(u.id)}
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 8px",
                              borderRadius: 7,
                              marginBottom: 4,
                              background: selectedUserId === u.id ? "#e6f0fa" : "#fafafa",
                              border: selectedUserId === u.id ? `1px solid ${ACCENT}` : "1px solid #f0f0f0",
                              transition: "all 0.12s",
                            }}
                          >
                            <Avatar
                              size={26}
                              icon={<UserOutlined />}
                              style={{
                                background: selectedUserId === u.id ? ACCENT : "#d9d9d9",
                                flexShrink: 0,
                                fontSize: 12,
                              }}
                            />
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: 12.5,
                                  fontWeight: 600,
                                  color: "#262626",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {u.name}
                              </div>
                              <div style={{ fontSize: 11, color: "#8c8c8c" }}>@{u.uname} · ID {u.id}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </Spin>
            </Card>
          </Col>

          {/* Right: page toggles for selected user */}
          <Col xs={24} lg={17} xl={18} xxl={19}>
            {!selectedUserId ? (
              <Card bordered={false} size="small" style={{ borderRadius: 10, textAlign: "center", padding: "30px 0" }}>
                <Empty description="Select a user on the left to manage their page access" />
              </Card>
            ) : (
              <Spin spinning={loadingPermissions}>
                <Card
                  bordered={false}
                  size="small"
                  bodyStyle={{ padding: "10px 14px" }}
                  style={{
                    borderRadius: 10,
                    marginBottom: 10,
                    background: "linear-gradient(90deg, #eef4fb 0%, #ffffff 65%)",
                    border: "1px solid #cddcec",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar size={32} icon={<UserOutlined />} style={{ background: ACCENT, flexShrink: 0 }} />
                    <div>
                      <Title level={5} style={{ margin: 0, fontSize: 14.5 }}>
                        {selectedUser?.name}
                      </Title>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        @{selectedUser?.uname} · ID {selectedUser?.id} ·{" "}
                        {ROLE_LABELS[(selectedUser?.type || "").toLowerCase()] || selectedUser?.type}
                      </Text>
                    </div>
                  </div>
                </Card>

                {Object.entries(pagesByCategory).map(([category, categoryPages]) => (
                  <Card
                    key={category}
                    bordered={false}
                    size="small"
                    bodyStyle={{ padding: "8px 12px" }}
                    style={{ borderRadius: 10, marginBottom: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
                    title={
                      <Text strong style={{ fontSize: 13 }}>
                        {category}{" "}
                        <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
                          · {categoryPages.length}
                        </Text>
                      </Text>
                    }
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: 6,
                      }}
                    >
                      {categoryPages.map((page) => {
                        const value = effectiveValue(page);
                        const overridden = isOverridden(page);
                        const on = !!value;
                        return (
                          <div
                            key={page.code}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 12,
                              padding: "7px 10px",
                              borderRadius: 8,
                              background: on ? "#f6ffed" : "#fff1f0",
                              border: `1px solid ${on ? "#b7eb8f" : "#ffccc7"}`,
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Space size={6} align="center" wrap>
                                <Text strong style={{ fontSize: 12.5 }}>
                                  {page.label}
                                </Text>
                                {overridden && (
                                  <Tag color="orange" style={{ borderRadius: 20, fontSize: 10, margin: 0, lineHeight: "16px" }}>
                                    Custom
                                  </Tag>
                                )}
                              </Space>
                              {page.description && (
                                <div style={{ fontSize: 11.5, color: "#8c8c8c", marginTop: 1 }}>
                                  <InfoCircleOutlined style={{ marginRight: 4 }} />
                                  {page.description}
                                </div>
                              )}
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                              {overridden && (
                                <Tooltip title="Reset to role default">
                                  <Button
                                    size="small"
                                    type="text"
                                    icon={<UndoOutlined />}
                                    onClick={() => handleResetOne(page)}
                                  />
                                </Tooltip>
                              )}
                              <Switch
                                size="small"
                                checked={on}
                                onChange={(checked) => handleToggle(page, checked)}
                                style={{ backgroundColor: on ? "#52c41a" : undefined }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                ))}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <Button size="small" icon={<UndoOutlined />} onClick={handleResetAll} disabled={loadingPermissions}>
                    Reset All to Role Default
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={saving}
                    disabled={!hasPendingChanges}
                    style={{ background: ACCENT, borderColor: ACCENT }}
                  >
                    Save Changes
                  </Button>
                </div>
              </Spin>
            )}
          </Col>
        </Row>
      </div>
    </Layout>
  );
}
